import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import { isStartCompanyBundle } from './lib/public-bundle'
import { IndexLanding, RootLayout } from './root-shell'
import {
  AdminStaffProjectDetail,
  AdminStaffTaskDetail,
  AdminStaffTasks,
  SellerStaffProjectDetail,
  SellerStaffTaskDetail,
  SellerStaffTasks,
  SupplyStaffProjectDetail,
  SupplyStaffTaskDetail,
  SupplyStaffTasks,
} from './features/staff-tasks/routes'
import { AdminShell } from './features/admin/AdminShell'
import { parseAdminInboxSearch } from './features/admin/messaging/admin-inbox-search'
import { AccountPage } from './features/account/AccountPage'
import { SellerShell, SellerHome } from './features/seller/SellerShell'
import { parseSellerListingsSearch } from './features/seller/my-listings-search'
import { SupplyShell, SupplyHome } from './features/supply/SupplyShell'
import { WorkshopPreparePack } from './features/supply/WorkshopPreparePack'
import { MySubmittedUpdates } from './features/supply/MySubmittedUpdates'
import { SubmitNewUpdate } from './features/supply/SubmitNewUpdate'
import { MyWorkOrders } from './features/supply/MyWorkOrders'
import { WorkOrderDetail } from './features/supply/WorkOrderDetail'
import { SupplyPurchaseOrderInbox } from './features/supply/SupplyPurchaseOrderInbox'
import { TodaysPrintQueue } from './features/supply/TodaysPrintQueue'
import { EmployeeManagementEmbed } from './components/EmployeeManagementEmbed'
import {
  ActivityAdmin,
  ActivitySeller,
  ActivitySupply,
} from './features/activity/ActivityFeed'
import { Settings } from './features/settings/Settings'
import { normalizeCatalogSearch, normalizeProductDetailSearch } from './features/admin/catalog/catalog-directory-state'
import { parseOnboardingListSearch } from './features/admin/onboarding/OnboardingListFilters'
import {
  AdminSamplesList,
  AdminSamplesBulkAdopt,
  AdminSamplesTrash,
  AdminSampleDetail,
  SupplySamplesList,
  SupplySampleDetail,
} from './features/samples/routes'
import { AcceptInvitationPage } from './features/onboarding/AcceptInvitationPage'
import { SelfApplyPage } from './features/onboarding/SelfApplyPage'
import { StartCompanyPage } from './features/start-company/StartCompanyPage'
import { WelcomePage } from './features/onboarding/WelcomePage'
import { LoginPage } from './features/auth/LoginPage'
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './features/auth/ResetPasswordPage'
// DriveAuditPage retired as the /admin/drive home (now DriveExplorerPage); since
// 2026-10-05 it is the "File activity" tab beside the Explorer (All Files).
import { PersonalDrivePage } from './features/drive/PersonalDrivePage'
import { ResourcesPage } from './features/shared/ResourcesPage'
import { EtsyProfitCalculator } from './features/shared/EtsyProfitCalculator'
import { ResearchGate } from './features/etsy-research/ResearchGate'
import { researchSearch } from './features/etsy-research/research-route'
import { MyPermissionsPage } from './features/shared/MyPermissionsPage'
import { AdminSettingsShell } from './features/admin/AdminSettingsShell'

// OPT-20261031-06: route pages (admin, seller, wallet, etsy) are split per
// route so a seller never downloads the admin pages. Shells, guards and auth
// pages stay static. Local names match the old static imports.
const AdminHome = lazyRouteComponent(
  () => import('./features/admin/AdminHome'),
  'AdminHome',
)
const PendingUpdatesQueue = lazyRouteComponent(
  () => import('./features/admin/PendingUpdatesQueue'),
  'PendingUpdatesQueue',
)
const PendingUpdateDetail = lazyRouteComponent(
  () => import('./features/admin/PendingUpdateDetail'),
  'PendingUpdateDetail',
)
const VendorReplacements = lazyRouteComponent(
  () => import('./features/admin/VendorReplacements'),
  'VendorReplacements',
)
const NewVendorReplacement = lazyRouteComponent(
  () => import('./features/admin/NewVendorReplacement'),
  'NewVendorReplacement',
)
const VendorReplacementDetail = lazyRouteComponent(
  () => import('./features/admin/VendorReplacementDetail'),
  'VendorReplacementDetail',
)
const Assortment = lazyRouteComponent(
  () => import('./features/admin/Assortment'),
  'Assortment',
)
const B2BPushList = lazyRouteComponent(
  () => import('./features/admin/B2BPushList'),
  'B2BPushList',
)
const NewB2BPackage = lazyRouteComponent(
  () => import('./features/admin/NewB2BPackage'),
  'NewB2BPackage',
)
const B2BPushDetail = lazyRouteComponent(
  () => import('./features/admin/B2BPushDetail'),
  'B2BPushDetail',
)
const BO3PortalPreview = lazyRouteComponent(
  () => import('./features/admin/BO3PortalPreview'),
  'BO3PortalPreview',
)
const CatalogIssues = lazyRouteComponent(
  () => import('./features/admin/CatalogIssues'),
  'CatalogIssues',
)
const DemoGuide = lazyRouteComponent(
  () => import('./features/admin/DemoGuide'),
  'DemoGuide',
)
const OrderManagement = lazyRouteComponent(
  () => import('./features/admin/OrderManagement'),
  'OrderManagement',
)
const AdminOrderDetail = lazyRouteComponent(
  () => import('./features/admin/OrderDetail'),
  'OrderDetail',
)
const AdminPrintLabel = lazyRouteComponent(
  () => import('./features/admin/PrintLabel'),
  'PrintLabel',
)
const MyReports = lazyRouteComponent(
  () => import('./features/seller/MyReports'),
  'MyReports',
)
const NewReport = lazyRouteComponent(
  () => import('./features/seller/NewReport'),
  'NewReport',
)
const OrderDetail = lazyRouteComponent(
  () => import('./features/seller/OrderDetail'),
  'OrderDetail',
)
const SellerInventory = lazyRouteComponent(
  () => import('./features/seller/Inventory'),
  'Inventory',
)
const ListInMyStore = lazyRouteComponent(
  () => import('./features/seller/ListInMyStore'),
  'ListInMyStore',
)
const SellerCatalog = lazyRouteComponent(
  () => import('./features/seller/catalog/SellerCatalog'),
  'SellerCatalog',
)
const SellerProductDetail = lazyRouteComponent(
  () => import('./features/seller/catalog/SellerProductDecisionDesk'),
  'SellerProductDetail',
)
const SellerProductDetailV2 = lazyRouteComponent(
  () => import('./features/seller/catalog/SellerProductDetailV2'),
  'SellerProductDetailV2',
)
const AdminInventory = lazyRouteComponent(
  () => import('./features/admin/Inventory'),
  'Inventory',
)
const PurchaseOrderAuthoring = lazyRouteComponent(
  () => import('./features/admin/PurchaseOrderAuthoring'),
  'PurchaseOrderAuthoring',
)
const PurchaseOrderList = lazyRouteComponent(
  () => import('./features/admin/PurchaseOrderList'),
  'PurchaseOrderList',
)
const PurchaseOrderDetail = lazyRouteComponent(
  () => import('./features/admin/PurchaseOrderDetail'),
  'PurchaseOrderDetail',
)
const PurchaseOrderReceiving = lazyRouteComponent(
  () => import('./features/admin/PurchaseOrderReceiving'),
  'PurchaseOrderReceiving',
)
const ReceivingScannerPage = lazyRouteComponent(
  () => import('./features/admin/ReceivingScannerPage'),
  'ReceivingScannerPage',
)
const PurchaseOrderQC = lazyRouteComponent(
  () => import('./features/admin/PurchaseOrderQC'),
  'PurchaseOrderQC',
)
const PurchaseOrderDispute = lazyRouteComponent(
  () => import('./features/admin/PurchaseOrderDispute'),
  'PurchaseOrderDispute',
)
const ReturnsQueue = lazyRouteComponent(
  () => import('./features/admin/ReturnsQueue'),
  'ReturnsQueue',
)
const ReturnProcessing = lazyRouteComponent(
  () => import('./features/admin/ReturnProcessing'),
  'ReturnProcessing',
)
const InventoryDashboard = lazyRouteComponent(
  () => import('./features/admin/InventoryDashboard'),
  'InventoryDashboard',
)
const HubReceive = lazyRouteComponent(
  () => import('./features/admin/HubReceive'),
  'HubReceive',
)
const ReportsDashboard = lazyRouteComponent(
  () => import('./features/admin/ReportsDashboard'),
  'ReportsDashboard',
)
const InventoryMovements = lazyRouteComponent(
  () => import('./features/admin/InventoryMovements'),
  'InventoryMovements',
)
const UnmappedSkuOperationalQueue = lazyRouteComponent(
  () => import('./features/admin/unmapped-skus/UnmappedSkuOperationalQueue'),
  'UnmappedSkuOperationalQueue',
)
const StockImportPage = lazyRouteComponent(
  () => import('./features/admin/inventory/StockImportPage'),
  'StockImportPage',
)
const CatalogSeedImportPage = lazyRouteComponent(
  () => import('./features/admin/inventory/CatalogSeedImportPage'),
  'CatalogSeedImportPage',
)
const UngroupedProductsPage = lazyRouteComponent(
  () => import('./features/admin/inventory/UngroupedProductsPage'),
  'UngroupedProductsPage',
)
const LegacyEtsyCleanupPage = lazyRouteComponent(
  () => import('./features/admin/inventory/LegacyEtsyCleanupPage'),
  'LegacyEtsyCleanupPage',
)
const BackorderQueue = lazyRouteComponent(
  () => import('./features/admin/inventory/BackorderQueue'),
  'BackorderQueue',
)
const ExternalFulfillmentReview = lazyRouteComponent(
  () => import('./features/admin/inventory/ExternalFulfillmentReview'),
  'ExternalFulfillmentReview',
)
const StockValuationPage = lazyRouteComponent(
  () => import('./features/admin/inventory/StockValuationPage'),
  'StockValuationPage',
)
const ProducerQueue = lazyRouteComponent(
  () => import('./features/admin/production/ProducerQueue'),
  'ProducerQueue',
)
const SellerStoresPage = lazyRouteComponent(
  () => import('./features/seller/stores/SellerStoresPage'),
  'SellerStoresPage',
)
const SellerStoreDetailPage = lazyRouteComponent(
  () => import('./features/seller/stores/SellerStoreDetailPage'),
  'SellerStoreDetailPage',
)
const ScorecardPage = lazyRouteComponent(
  () => import('./features/store-scorecard/ScorecardPage'),
  'ScorecardPage',
)
const MyProducts = lazyRouteComponent(
  () => import('./features/seller/MyProducts'),
  'MyProducts',
)
const MyListings = lazyRouteComponent(
  () => import('./features/seller/MyListings'),
  'MyListings',
)
const MyOrders = lazyRouteComponent(
  () => import('./features/seller/MyOrders'),
  'MyOrders',
)
const SellerReturns = lazyRouteComponent(
  () => import('./features/seller/SellerReturns'),
  'SellerReturns',
)
const Payments = lazyRouteComponent(
  () => import('./features/wallet/Payments'),
  'Payments',
)
const Referral = lazyRouteComponent(
  () => import('./features/wallet/Referral'),
  'Referral',
)
const BankingDetails = lazyRouteComponent(
  () => import('./features/wallet/BankingDetails'),
  'BankingDetails',
)
const SellerSubscriptionPage = lazyRouteComponent(
  () => import('./features/wallet/SubscriptionPage'),
  'SubscriptionPage',
)
const AddFunds = lazyRouteComponent(
  () => import('./features/wallet/AddFunds'),
  'AddFunds',
)
const PayoutSetup = lazyRouteComponent(
  () => import('./features/wallet/PayoutSetup'),
  'PayoutSetup',
)
const AdminWalletsPage = lazyRouteComponent(
  () => import('./features/admin/AdminWalletsPage'),
  'AdminWalletsPage',
)
const BankApprovals = lazyRouteComponent(
  () => import('./features/admin/BankApprovals'),
  'BankApprovals',
)
const PaymentGateways = lazyRouteComponent(
  () => import('./features/admin/PaymentGateways'),
  'PaymentGateways',
)
const BankPage = lazyRouteComponent(
  () => import('./features/admin/Bank/BankPage'),
  'BankPage',
)
const PoliciesPage = lazyRouteComponent(
  () => import('./features/admin/Policies/PoliciesPage'),
  'PoliciesPage',
)
const DesignStudioHome = lazyRouteComponent(
  () => import('./features/admin/personalize/DesignStudioHome'),
  'DesignStudioHome',
)
const DocStudioCatalogsPage = lazyRouteComponent(
  () => import('./features/admin/document-studio/CatalogsPage'),
  'CatalogsPage',
)
const DocStudioFilesPage = lazyRouteComponent(
  () => import('./features/admin/document-studio/GeneratedFilesPage'),
  'GeneratedFilesPage',
)
const DocStudioBrandSettingsPage = lazyRouteComponent(
  () => import('./features/admin/document-studio/BrandSettingsPage'),
  'BrandSettingsPage',
)
const DocStudioBrandPagesPage = lazyRouteComponent(
  () => import('./features/admin/document-studio/BrandPagesPage'),
  'BrandPagesPage',
)
const CategoriesPage = lazyRouteComponent(
  () => import('./features/admin/personalize/CategoriesPage'),
  'CategoriesPage',
)
const ProductListPage = lazyRouteComponent(
  () => import('./features/admin/catalog/ProductListPage'),
  'ProductListPage',
)
const BundlesPage = lazyRouteComponent(
  () => import('./features/admin/catalog/bundles/BundlesPage'),
  'BundlesPage',
)
const CatalogHubPage = lazyRouteComponent(
  () => import('./features/admin/catalog/CatalogHubPage'),
  'CatalogHubPage',
)
const ProductDetailPage = lazyRouteComponent(
  () => import('./features/admin/catalog/ProductDetailPage'),
  'ProductDetailPage',
)
const MeasurementQueuePage = lazyRouteComponent(
  () => import('./features/admin/catalog/MeasurementQueuePage'),
  'MeasurementQueuePage',
)
const MarketplaceListings = lazyRouteComponent(
  () => import('./features/admin/catalog/MarketplaceListings'),
  'MarketplaceListings',
)
const NormalizationPage = lazyRouteComponent(
  () => import('./features/admin/catalog/NormalizationPage'),
  'NormalizationPage',
)
const GirginImportPage = lazyRouteComponent(
  () => import('./features/admin/catalog/GirginImportPage'),
  'GirginImportPage',
)
const SkuAliasReviewPage = lazyRouteComponent(
  () => import('./features/admin/catalog/SkuAliasReviewPage'),
  'SkuAliasReviewPage',
)
const OnboardingListPage = lazyRouteComponent(
  () => import('./features/admin/onboarding/OnboardingListPage'),
  'OnboardingListPage',
)
const OnboardingDetailPage = lazyRouteComponent(
  () => import('./features/admin/onboarding/OnboardingDetailPage'),
  'OnboardingDetailPage',
)
const CompanyRegistrationsList = lazyRouteComponent(
  () => import('./features/admin/company-registrations/CompanyRegistrationsList'),
  'CompanyRegistrationsList',
)
const CompanyRegistrationDetail = lazyRouteComponent(
  () => import('./features/admin/company-registrations/CompanyRegistrationDetail'),
  'CompanyRegistrationDetail',
)
const SelfApplyReviewPage = lazyRouteComponent(
  () => import('./features/admin/SelfApplyReviewPage'),
  'SelfApplyReviewPage',
)
const PartnerSettlementPanel = lazyRouteComponent(
  () => import('./features/admin/PartnerSettlementPanel'),
  'PartnerSettlementPanel',
)
const ShippingHub = lazyRouteComponent(
  () => import('./features/admin/shipping/ShippingHub'),
  'ShippingHub',
)
const DriveTriagePage = lazyRouteComponent(
  () => import('./features/admin/DriveTriagePage'),
  'DriveTriagePage',
)
const ListingTriagePage = lazyRouteComponent(
  () => import('./features/admin/ListingTriagePage'),
  'ListingTriagePage',
)
const DriveMaintenancePage = lazyRouteComponent(
  () => import('./features/admin/DriveMaintenancePage'),
  'DriveMaintenancePage',
)
const ShippingDemoPage = lazyRouteComponent(
  () => import('./features/admin/shipping/ShippingDemoPage'),
  'ShippingDemoPage',
)
const SeraLiveDemoPage = lazyRouteComponent(
  () => import('./features/admin/shipping/SeraLiveDemoPage'),
  'SeraLiveDemoPage',
)
const DriveExplorerPage = lazyRouteComponent(
  () => import('./features/admin/drive/DriveExplorerPage'),
  'DriveExplorerPage',
)
const DriveAuditPage = lazyRouteComponent(
  () => import('./features/admin/DriveAuditPage'),
  'DriveAuditPage',
)
const SupplierHub = lazyRouteComponent(
  () => import('./features/admin/SupplierHub'),
  'SupplierHub',
)
const PricingHubPage = lazyRouteComponent(
  () => import('./features/admin/PricingHubPage'),
  'PricingHubPage',
)
const IncomingWiresPage = lazyRouteComponent(
  () => import('./features/admin/IncomingWiresPage'),
  'IncomingWiresPage',
)
const SubscriptionsPage = lazyRouteComponent(
  () => import('./features/admin/SubscriptionsPage'),
  'SubscriptionsPage',
)
const FeesAndChargesPage = lazyRouteComponent(
  () => import('./features/admin/FeesAndChargesPage'),
  'FeesAndChargesPage',
)
const PricingMatrixPage = lazyRouteComponent(
  () => import('./features/admin/pricing/PricingMatrixPage'),
  'PricingMatrixPage',
)
const KeywordSearchPage = lazyRouteComponent(
  () => import('./features/etsy-research/KeywordSearchPage'),
  'KeywordSearchPage',
)
const ListingResearchPage = lazyRouteComponent(
  () => import('./features/etsy-research/ListingResearchPage'),
  'ListingResearchPage',
)
const ShopResearchPage = lazyRouteComponent(
  () => import('./features/etsy-research/ShopResearchPage'),
  'ShopResearchPage',
)
const ShopReviewsPage = lazyRouteComponent(
  () => import('./features/etsy-research/ShopReviewsPage'),
  'ShopReviewsPage',
)
const TodayOpportunitiesPage = lazyRouteComponent(
  () => import('./features/etsy-research/TodayOpportunitiesPage'),
  'TodayOpportunitiesPage',
)
const EtsyTrendsPage = lazyRouteComponent(
  () => import('./features/etsy-trends/EtsyTrendsPage'),
  'EtsyTrendsPage',
)
const UsCalendarPage = lazyRouteComponent(
  () => import('./features/us-calendar/UsCalendarPage'),
  'UsCalendarPage',
)
const LeadsPage = lazyRouteComponent(
  () => import('./features/admin/leads/LeadsPage'),
  'LeadsPage',
)
const ScoreboardPage = lazyRouteComponent(
  () => import('./features/admin/leads/ScoreboardPage'),
  'ScoreboardPage',
)
const LeadDetailPage = lazyRouteComponent(
  () => import('./features/admin/leads/LeadDetailPage'),
  'LeadDetailPage',
)
const TrainingsPage = lazyRouteComponent(
  () => import('./features/trainings/TrainingsPage'),
  'TrainingsPage',
)
const AdminUsersPage = lazyRouteComponent(
  () => import('./features/admin/AdminUsersPage'),
  'AdminUsersPage',
)
const FulfillmentEcosystems = lazyRouteComponent(
  () => import('./features/admin/FulfillmentEcosystems'),
  'FulfillmentEcosystems',
)
const MultiStoreReport = lazyRouteComponent(
  () => import('./features/seller/MultiStoreReport'),
  'MultiStoreReport',
)
const FontsLibrary = lazyRouteComponent(
  () => import('./features/admin/personalize/FontsLibrary'),
  'FontsLibrary',
)
const FontApprovalQueue = lazyRouteComponent(
  () => import('./features/admin/personalize/FontApprovalQueue'),
  'FontApprovalQueue',
)
const MtsFontsPage = lazyRouteComponent(
  () => import('./features/admin/personalize/MtsFontsPage'),
  'MtsFontsPage',
)
const AccessGrantGrid = lazyRouteComponent(
  () => import('./features/admin/personalize/AccessGrantGrid'),
  'AccessGrantGrid',
)
const SellerReviews = lazyRouteComponent(
  () => import('./features/seller/Reviews'),
  'Reviews',
)
const AdminReviews = lazyRouteComponent(
  () => import('./features/admin/Reviews'),
  'Reviews',
)
const ReviewTrackingSettings = lazyRouteComponent(
  () => import('./features/admin/ReviewTrackingSettings'),
  'ReviewTrackingSettings',
)
const SellerPersonalizeHome = lazyRouteComponent(
  () => import('./features/seller/PersonalizeHome'),
  'PersonalizeHome',
)
const SellerPersonalizeTemplates = lazyRouteComponent(
  () => import('./features/seller/PersonalizeTemplates'),
  'PersonalizeTemplates',
)
const SellerPersonalizeBindings = lazyRouteComponent(
  () => import('./features/seller/PersonalizeBindings'),
  'PersonalizeBindings',
)

// Design Studio canvas editors pull in fabric (~300KB) + opentype.js (~200KB).
// Lazy-split so the half-megabyte canvas payload loads only when an operator
// actually enters the SVG editor / composer, not on every admin first paint.
const SvgEditor = lazyRouteComponent(
  () => import('./features/admin/personalize/SvgEditor'),
  'SvgEditor',
)
const Composer = lazyRouteComponent(
  () => import('./features/admin/personalize/Composer'),
  'Composer',
)
// Listing Factory draft page pulls the scene wizard + image gallery; lazy-split
// so the generate/wizard surface loads only when a seller opens a draft.
const DraftListingPage = lazyRouteComponent(
  () => import('./features/seller/DraftListingPage'),
  'DraftListingPage',
)
// Scene Builder hosts a 3-step wizard dialog + option grids; lazy-split so it
// loads only when a seller opens the Listing Factory scene tool.
const SceneBuilder = lazyRouteComponent(
  () => import('./features/seller/SceneBuilder'),
  'SceneBuilder',
)
const AdminInboxPage = lazyRouteComponent(
  () => import('./features/admin/messaging/AdminInboxPage'),
  'Component',
)
const SellerMessagesPage = lazyRouteComponent(
  () => import('./features/seller/messaging/SellerMessagesPage'),
  'Component',
)
const SupplyMessagesPage = lazyRouteComponent(
  () => import('./features/supply/messaging/SupplyMessagesPage'),
  'Component',
)
const ExitPlanPage = lazyRouteComponent(
  () => import('./features/admin/exit-plan/ExitPlanPage'),
  'ExitPlanPage',
)
const SalesGoalsPage = lazyRouteComponent(
  () => import('./features/admin/sales-goals/SalesGoalsPage'),
  'SalesGoalsPage',
)
const ServersPage = lazyRouteComponent(
  () => import('./features/admin/servers/ServersPage'),
)
const SellerServersPage = lazyRouteComponent(
  () => import('./features/seller/servers/SellerServersPage'),
)
const AdminStoresPage = lazyRouteComponent(
  () => import('./features/admin/stores/AdminStoresPage'),
)

// True only in the standalone public "Start a U.S. company" wizard bundle
// (VITE_BASE_PATH=/start-company/). It makes the router mount ONLY the wizard at
// "/" with no auth guard and no app shell; false in every main app build. See
// lib/public-bundle.ts for the full rationale.
export const IS_START_COMPANY_BUNDLE = isStartCompanyBundle()

const rootRoute = createRootRoute({ component: RootLayout })

// Landing at the app root. In real-engine mode a logged-in user is bounced to
// their D-7 shell (same role-aware landing as LoginPage) so /app/ never dead-ends
// on the maket hint; without a token the auth guard already forces /login. In
// maket/simulation mode the mock role-switcher drives, so show its hint.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  // In the public wizard bundle the browser URL /start-company/ resolves to "/"
  // (basepath stripped), so the index route IS the wizard. The main app build
  // keeps the auth-aware role landing. Route shape/types are identical either
  // way — only the rendered component differs.
  component: IS_START_COMPANY_BUNDLE ? StartCompanyPage : IndexLanding,
})

// ─── Public Onboarding routes (Priority 7 Day 4 — invite-only V1) ────────────
// No auth required. Direct children of rootRoute (no shell wrapper).
const publicAcceptInvitation = createRoute({
  getParentRoute: () => rootRoute,
  path: 'accept/$token',
  component: AcceptInvitationPage,
})
// Public self-apply form — prospective external partner applies to join (BE-54 V1.5).
const publicSelfApply = createRoute({
  getParentRoute: () => rootRoute,
  path: 'apply',
  component: SelfApplyPage,
})
// Public "Start a U.S. company" wizard — no auth, no shell (mirrors /apply).
// Canonical path is /start-company; /start-a-company is kept as an alias route so
// a direct SPA hit on the old path still renders (prod also 301s it at nginx).
const publicStartCompany = createRoute({
  getParentRoute: () => rootRoute,
  path: 'start-company',
  component: StartCompanyPage,
})
const publicStartCompanyAlias = createRoute({
  getParentRoute: () => rootRoute,
  path: 'start-a-company',
  component: StartCompanyPage,
})
// Real-auth login (cutover). Public, no shell wrapper. In maket mode the
// role-switch still drives the UI; this is the entry for real-engine mode.
const publicLogin = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login',
  component: LoginPage,
})

// Public forgot-password entry. No shell wrapper, no token required.
const publicForgotPassword = createRoute({
  getParentRoute: () => rootRoute,
  path: 'forgot-password',
  component: ForgotPasswordPage,
})

// Public reset-password page. The 1h reset JWT arrives via the `token` search
// param from the emailed link (https://pexify.net/app/reset-password?token=...).
const publicResetPassword = createRoute({
  getParentRoute: () => rootRoute,
  path: 'reset-password',
  validateSearch: (
    search: Record<string, unknown>,
  ): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: ResetPasswordPage,
})

const publicWelcome = createRoute({
  getParentRoute: () => rootRoute,
  path: 'onboarding/welcome',
  // Search params carry the welcome context (org_type / org_name / org_scope /
  // email) from AcceptInvitationPage. V1 sim; real auth M3 resume → session.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    org_type?: string
    org_name?: string
    org_scope?: string
    email?: string
  } => ({
    org_type: typeof search.org_type === 'string' ? search.org_type : undefined,
    org_name: typeof search.org_name === 'string' ? search.org_name : undefined,
    org_scope: typeof search.org_scope === 'string' ? search.org_scope : undefined,
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
  component: WelcomePage,
})

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'admin',
  component: AdminShell,
})
const adminIndex = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: AdminHome,
})
const adminInbox = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inbox',
  validateSearch: parseAdminInboxSearch,
  component: AdminInboxPage,
})
const adminServers = createRoute({
  getParentRoute: () => adminRoute,
  path: 'servers',
  component: ServersPage,
})
const adminStores = createRoute({
  getParentRoute: () => adminRoute,
  path: 'stores',
  // ?q= pre-fills the store search (the topbar search links stores here).
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === 'string' && search.q.trim() ? search.q : undefined,
  }),
  component: AdminStoresPage,
})
const adminScorecard = createRoute({
  getParentRoute: () => adminRoute,
  path: 'scorecard',
  component: () => <ScorecardPage scope="admin" />,
})
const adminHubReceiving = createRoute({
  getParentRoute: () => adminRoute,
  path: 'hub-receiving',
  component: HubReceive,
})
const adminAccount = createRoute({
  getParentRoute: () => adminRoute,
  path: 'account',
  component: () => <AccountPage shell="admin" />,
})
const adminPendingUpdates = createRoute({
  getParentRoute: () => adminRoute,
  path: 'pending-updates',
  component: PendingUpdatesQueue,
})
const adminPendingUpdateDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'pending-updates/$id',
  component: PendingUpdateDetail,
})
const adminVendorReplacements = createRoute({
  getParentRoute: () => adminRoute,
  path: 'vendor-replacements',
  component: VendorReplacements,
})
const adminVendorReplacementNew = createRoute({
  getParentRoute: () => adminRoute,
  path: 'vendor-replacements/new',
  component: NewVendorReplacement,
})
const adminVendorReplacementDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'vendor-replacements/$id',
  component: VendorReplacementDetail,
})
const adminAssortment = createRoute({
  getParentRoute: () => adminRoute,
  path: 'assortment',
  component: Assortment,
})
const adminB2BPushList = createRoute({
  getParentRoute: () => adminRoute,
  path: 'b2b-push',
  component: B2BPushList,
})
const adminB2BPushNew = createRoute({
  getParentRoute: () => adminRoute,
  path: 'b2b-push/new',
  component: NewB2BPackage,
})
const adminB2BPushDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'b2b-push/$id',
  component: B2BPushDetail,
})
const adminBO3PortalPreview = createRoute({
  getParentRoute: () => adminRoute,
  path: 'b2b-portal-preview/$buyer_id',
  component: BO3PortalPreview,
})
const adminCatalogIssues = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog-issues',
  component: CatalogIssues,
})
const adminDemoGuide = createRoute({
  getParentRoute: () => adminRoute,
  path: 'demo-guide',
  component: DemoGuide,
})
const adminInventory = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory',
  component: InventoryDashboard,
})
const adminInventoryProducts = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory/products',
  component: AdminInventory,
})
const adminInventoryMovements = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory/movements',
  component: InventoryMovements,
})
const adminUnmappedSkus = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory/unmapped',
  component: UnmappedSkuOperationalQueue,
})
const adminInventoryImport = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory/import',
  component: StockImportPage,
})
const adminCatalogSeedImport = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory/seed-import',
  component: CatalogSeedImportPage,
})
const adminUngroupedProducts = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory/ungrouped',
  component: UngroupedProductsPage,
})
const adminLegacyEtsyCleanup = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory/legacy-etsy-cleanup',
  component: LegacyEtsyCleanupPage,
})
const adminInventoryBackorders = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory/backorders',
  component: BackorderQueue,
})
const adminInventoryExternalFulfillment = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory/external-fulfillment',
  component: ExternalFulfillmentReview,
})
const adminInventoryValuation = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inventory/valuation',
  component: StockValuationPage,
})
const adminProducerQueue = createRoute({
  getParentRoute: () => adminRoute,
  path: 'production/producer-queue',
  component: ProducerQueue,
})
const adminOrders = createRoute({
  getParentRoute: () => adminRoute,
  path: 'orders',
  component: OrderManagement,
})
// Phase 1B Group 2 (wave-13-day-2) — Procurement authoring entry.
const adminPONew = createRoute({
  getParentRoute: () => adminRoute,
  path: 'po/new',
  component: PurchaseOrderAuthoring,
})
// Phase 1B Group 3 (wave-13-day-3) — PO List operator scan surface.
const adminPOList = createRoute({
  getParentRoute: () => adminRoute,
  path: 'po',
  component: PurchaseOrderList,
})
// Phase 1B Group 3 (wave-13-day-3) — PO Detail single-PO inspection.
const adminPODetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'po/$id',
  component: PurchaseOrderDetail,
})
// Phase 1B Group 4 (wave-13-day-4) — PO Receiving session form.
// Wave-14-day-4 (Kapı 2 ratify 2026-05-31): legacy /receive STAYS alive
// behind a deprecation banner; new /receive-scan route is the canonical V2
// 2-step scan flow. Wave-15 cutover removes /receive.
const adminPOReceive = createRoute({
  getParentRoute: () => adminRoute,
  path: 'po/$id/receive',
  component: PurchaseOrderReceiving,
})
// Wave-14-day-4 — new 2-step receive flow (PO/Batch scan → Product Barcode scan).
const adminPOReceiveScan = createRoute({
  getParentRoute: () => adminRoute,
  path: 'po/$id/receive-scan',
  component: ReceivingScannerPage,
})
// Phase 1B Group 5 (wave-13-day-5) — PO QC split (pass / quarantine).
const adminPOQC = createRoute({
  getParentRoute: () => adminRoute,
  path: 'po/$id/qc',
  component: PurchaseOrderQC,
})
// Phase 1B Group 6 (wave-13-day-6) — PO Dispute thread + dispositions.
const adminPODispute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'po/$id/dispute',
  component: PurchaseOrderDispute,
})
// Dalga 3 (envanter akışı denetimi 2026-09-26) — returns queue + wizard.
const adminReturnsQueue = createRoute({
  getParentRoute: () => adminRoute,
  path: 'returns',
  component: ReturnsQueue,
})
const adminReturnProcessing = createRoute({
  getParentRoute: () => adminRoute,
  path: 'returns/$id',
  component: ReturnProcessing,
})
const adminOrderDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'orders/$id',
  component: AdminOrderDetail,
})
const adminWorkOrderPrintLabel = createRoute({
  getParentRoute: () => adminRoute,
  path: 'work-orders/$id/print-label',
  component: AdminPrintLabel,
})
const adminWorkspaceTasks = createRoute({
  getParentRoute: () => adminRoute,
  path: 'workspace/tasks',
  component: AdminStaffTasks,
})
const adminWorkspaceTaskDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'workspace/tasks/$taskId',
  component: AdminStaffTaskDetail,
})
const adminWorkspaceProjectDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'workspace/tasks/projects/$projectId',
  component: AdminStaffProjectDetail,
})
const adminWalletIndex = createRoute({
  getParentRoute: () => adminRoute,
  path: 'wallet',
  component: Payments,
})
const adminWalletPayments = createRoute({
  getParentRoute: () => adminRoute,
  path: 'wallet/payments',
  component: Payments,
})
const adminWalletReferral = createRoute({
  getParentRoute: () => adminRoute,
  path: 'wallet/referral',
  component: Referral,
})
const adminBankApprovals = createRoute({
  getParentRoute: () => adminRoute,
  path: 'wallet/bank-approvals',
  component: BankApprovals,
})
const adminPaymentGateways = createRoute({
  getParentRoute: () => adminRoute,
  path: 'wallet/payment-gateways',
  component: PaymentGateways,
})
const adminBank = createRoute({
  getParentRoute: () => adminRoute,
  path: 'bank',
  component: BankPage,
})
const adminPolicies = createRoute({
  getParentRoute: () => adminRoute,
  path: 'policies',
  component: PoliciesPage,
})
const adminActivity = createRoute({
  getParentRoute: () => adminRoute,
  path: 'activity',
  component: ActivityAdmin,
})
const adminReports = createRoute({
  getParentRoute: () => adminRoute,
  path: 'reports',
  component: ReportsDashboard,
})
const adminEmployeeManagement = createRoute({
  getParentRoute: () => adminRoute,
  path: 'employee-management',
  component: EmployeeManagementEmbed,
})
const adminSettings = createRoute({
  getParentRoute: () => adminRoute,
  path: 'settings',
  component: AdminSettingsShell,
})

// ─── Catalog (Listing Generator Wave 1 — admin master_catalog CRUD) ──────────
// Volkan A1 admin-owned master catalog management (D-6 Resource Ownership).
// Cross-cuts with Drive Priority 11 (Product Images section) Day 6-7.
const adminCatalogBundles = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/bundles',
  component: BundlesPage,
})

const adminCatalogProducts = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/products',
  validateSearch: normalizeCatalogSearch,
  component: ProductListPage,
})
const adminCatalogCategories = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/categories',
  component: CatalogHubPage,
})
const adminCatalogProductDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/products/$id',
  validateSearch: normalizeProductDetailSearch,
  component: ProductDetailPage,
})
const adminCatalogMeasurementQueue = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/measurement-queue',
  component: MeasurementQueuePage,
})
const adminCatalogMarketplaceListings = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/marketplace-listings',
  component: MarketplaceListings,
})
const adminCatalogNormalization = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/normalization',
  component: NormalizationPage,
})
const adminGirginImport = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/girgin-import',
  component: GirginImportPage,
})
const adminCatalogSkuAliases = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/sku-aliases',
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; store_integration_id?: string } => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    store_integration_id:
      typeof search.store_integration_id === 'string'
        ? search.store_integration_id
        : undefined,
  }),
  component: SkuAliasReviewPage,
})

// ─── Onboarding (Priority 7 V1 invite-only) ──────────────────────────────────
const adminOnboardingList = createRoute({
  getParentRoute: () => adminRoute,
  path: 'onboarding',
  validateSearch: parseOnboardingListSearch,
  component: OnboardingListPage,
})
const adminOnboardingDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'onboarding/$id',
  validateSearch: parseOnboardingListSearch,
  component: OnboardingDetailPage,
})
// Sample domain (Sampler port) — admin = customer.
const adminSamplesList = createRoute({
  getParentRoute: () => adminRoute,
  path: 'samples',
  component: AdminSamplesList,
})
const adminSamplesTrash = createRoute({
  getParentRoute: () => adminRoute,
  path: 'samples/trash',
  component: AdminSamplesTrash,
})
const adminSamplesBulkAdopt = createRoute({
  getParentRoute: () => adminRoute,
  path: 'samples/bulk-adopt',
  // `ids` carries the rows ticked on the samples list; absent = the whole shelf.
  validateSearch: (search: Record<string, unknown>) => ({
    ids: typeof search.ids === 'string' && search.ids ? search.ids : undefined,
  }),
  component: AdminSamplesBulkAdopt,
})
const adminSampleDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'samples/$id',
  component: AdminSampleDetail,
})
// Self-Apply review queue — qualify inbound partner applications (BE-54 V1.5).
const adminSelfApplyList = createRoute({
  getParentRoute: () => adminRoute,
  path: 'self-apply',
  component: SelfApplyReviewPage,
})
const adminPartnerSettlements = createRoute({
  getParentRoute: () => adminRoute,
  path: 'partner-settlements',
  component: PartnerSettlementPanel,
})
// Fees & take rates moved to Finance → Fees & charges (Volkan 2026-10-26); the
// old address still opens them there.
const adminMonetization = createRoute({
  getParentRoute: () => adminRoute,
  path: 'monetization',
  beforeLoad: () => {
    throw redirect({ to: '/admin/finance/fees' })
  },
})
const adminFeesAndCharges = createRoute({
  getParentRoute: () => adminRoute,
  path: 'finance/fees',
  component: FeesAndChargesPage,
})
// Bank wire / ACH top-ups through PEXIFY's Mercury account (mig 384).
const adminIncomingWires = createRoute({
  getParentRoute: () => adminRoute,
  path: 'finance/incoming-wires',
  component: IncomingWiresPage,
})
// Seller monthly subscriptions (Volkan 2026-11-01): start trials, apply a new
// price, cancel one. The price is edited on Fees & charges.
const adminSubscriptions = createRoute({
  getParentRoute: () => adminRoute,
  path: 'finance/subscriptions',
  component: SubscriptionsPage,
})
const adminShippingSetup = createRoute({
  getParentRoute: () => adminRoute,
  path: 'shipping-setup',
  component: ShippingHub,
})
const adminDriveTriage = createRoute({
  getParentRoute: () => adminRoute,
  path: 'drive-triage',
  component: DriveTriagePage,
})
const adminListingTriage = createRoute({
  getParentRoute: () => adminRoute,
  path: 'listing-triage',
  component: ListingTriagePage,
})
const adminDriveMaintenance = createRoute({
  getParentRoute: () => adminRoute,
  path: 'drive-maintenance',
  component: DriveMaintenancePage,
})
const adminShippingDemo = createRoute({
  getParentRoute: () => adminRoute,
  path: 'shipping-demo',
  component: ShippingDemoPage,
})
const adminShippingLiveDemo = createRoute({
  getParentRoute: () => adminRoute,
  path: 'shipping-live-demo',
  component: SeraLiveDemoPage,
})

// Drive Explorer — read-only governance file browser (Ring 3). Admin-only
// oversight tool; lives under the unified Drive as a sub-item (the Drive home
// itself is now the file-manager PersonalDrivePage below).
const adminDriveExplorer = createRoute({
  getParentRoute: () => adminRoute,
  path: 'drive-explorer',
  component: DriveExplorerPage,
})
// File activity (cross-org asset list + who-did-what log) — moved out of
// Settings to sit as a tab beside the Explorer (2026-10-05).
const adminDriveAudit = createRoute({
  getParentRoute: () => adminRoute,
  path: 'drive-audit',
  component: DriveAuditPage,
})
// Supplier catalogue curation (Printify / JDS / Gelato discovery + sync) is
// daily catalogue work, so it left Settings for the Catalog menu (2026-10-05).
const adminSupplierCatalog = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/suppliers',
  component: SupplierHub,
})

// Drive — the unified file-manager (personal/team + read-only operational roots).
// One shared PersonalDrivePage is the Drive home in all three shells.
const adminDriveFiles = createRoute({
  getParentRoute: () => adminRoute,
  path: 'drive',
  component: PersonalDrivePage,
})
const sellerDriveFiles = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'drive',
  component: PersonalDrivePage,
})
const supplyDriveFiles = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'drive',
  component: PersonalDrivePage,
})

// Wave 2 Day 2: Admin Pricing Hub (global multipliers + cost-range rules).
// Its own menu entry since 2026-10-26 (Volkan): /admin/pricing; the old
// Catalog address redirects there.
const adminPricing = createRoute({
  getParentRoute: () => adminRoute,
  path: 'pricing',
  component: PricingHubPage,
})
const adminPricingHub = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/pricing-hub',
  beforeLoad: () => {
    throw redirect({ to: '/admin/pricing' })
  },
})

// D-14 FULL Phase 2: the Pricing Matrix — the whole catalogue's costs in one grid
const adminPricingMatrix = createRoute({
  getParentRoute: () => adminRoute,
  path: 'catalog/pricing-matrix',
  component: PricingMatrixPage,
})

// Etsy Profit Calculator — shared admin + seller tool (seller-typed costs, no supplier leak)
const adminEtsyProfit = createRoute({
  getParentRoute: () => adminRoute,
  path: 'tools/etsy-profit',
  component: () => <EtsyProfitCalculator role="admin" />,
})
const sellerEtsyProfit = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'tools/etsy-profit',
  component: () => <EtsyProfitCalculator role="seller" />,
})
// Etsy Trends — nightly Etsy niche measurements (admin + seller, mig 366)
const adminEtsyTrends = createRoute({
  getParentRoute: () => adminRoute,
  path: 'tools/etsy-trends',
  component: () => <EtsyTrendsPage scope="admin" />,
})
const sellerEtsyTrends = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'tools/etsy-trends',
  component: () => <EtsyTrendsPage scope="seller" />,
})
// US event calendar — US shopping days and holidays with their preparation
// window (admin + seller, mig 374)
const adminUsCalendar = createRoute({
  getParentRoute: () => adminRoute,
  path: 'tools/us-calendar',
  component: () => <UsCalendarPage scope="admin" />,
})
const sellerUsCalendar = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'tools/us-calendar',
  component: () => <UsCalendarPage scope="seller" />,
})
// Leads — the team's seller / maker candidates (mig 391, leads.use); filters
// live in the URL (parseLeadsSearch).
const adminLeads = createRoute({
  getParentRoute: () => adminRoute,
  path: 'leads',
  component: LeadsPage,
})
// Monthly scoreboard (?month=YYYY-MM; current month by default).
const adminLeadsScoreboard = createRoute({
  getParentRoute: () => adminRoute,
  path: 'leads/scoreboard',
  component: ScoreboardPage,
})
// One lead: details, stage, contact log, stage history (no hub strip).
const adminLeadDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'leads/$id',
  component: LeadDetailPage,
})

// Trainings — how-to articles for the team; ?article=<id> is the open one
// (mig 388)
const adminTrainings = createRoute({
  getParentRoute: () => adminRoute,
  path: 'trainings',
  validateSearch: (search: Record<string, unknown>): { article?: string } => ({
    article: typeof search.article === 'string' ? search.article : undefined,
  }),
  component: TrainingsPage,
})

// Etsy research — public keyword / shop / review / listing lookups and today's
// opportunities, shared admin + seller
const adminEtsyResearchKeyword = createRoute({
  getParentRoute: () => adminRoute,
  path: 'tools/etsy-research/keyword',
  validateSearch: researchSearch,
  component: () => <ResearchGate scope="admin"><KeywordSearchPage scope="admin" /></ResearchGate>,
})
const adminEtsyResearchToday = createRoute({
  getParentRoute: () => adminRoute,
  path: 'tools/etsy-research/today',
  component: () => <ResearchGate scope="admin"><TodayOpportunitiesPage scope="admin" /></ResearchGate>,
})
const sellerEtsyResearchKeyword = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'tools/etsy-research/keyword',
  validateSearch: researchSearch,
  component: () => <ResearchGate scope="seller"><KeywordSearchPage scope="seller" /></ResearchGate>,
})
const sellerEtsyResearchToday = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'tools/etsy-research/today',
  component: () => <ResearchGate scope="seller"><TodayOpportunitiesPage scope="seller" /></ResearchGate>,
})
const adminEtsyResearchShop = createRoute({
  getParentRoute: () => adminRoute,
  path: 'tools/etsy-research/shop',
  validateSearch: researchSearch,
  component: () => <ResearchGate scope="admin"><ShopResearchPage scope="admin" /></ResearchGate>,
})
const adminEtsyResearchReviews = createRoute({
  getParentRoute: () => adminRoute,
  path: 'tools/etsy-research/reviews',
  validateSearch: researchSearch,
  component: () => <ResearchGate scope="admin"><ShopReviewsPage scope="admin" /></ResearchGate>,
})
const adminEtsyResearchListing = createRoute({
  getParentRoute: () => adminRoute,
  path: 'tools/etsy-research/listing',
  validateSearch: researchSearch,
  component: () => <ResearchGate scope="admin"><ListingResearchPage scope="admin" /></ResearchGate>,
})
const sellerEtsyResearchShop = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'tools/etsy-research/shop',
  validateSearch: researchSearch,
  component: () => <ResearchGate scope="seller"><ShopResearchPage scope="seller" /></ResearchGate>,
})
const sellerEtsyResearchReviews = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'tools/etsy-research/reviews',
  validateSearch: researchSearch,
  component: () => <ResearchGate scope="seller"><ShopReviewsPage scope="seller" /></ResearchGate>,
})
const sellerEtsyResearchListing = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'tools/etsy-research/listing',
  validateSearch: researchSearch,
  component: () => <ResearchGate scope="seller"><ListingResearchPage scope="seller" /></ResearchGate>,
})

// Wave 6 Day 1: Resources Library (admin / seller / supply shells)
const adminResources = createRoute({
  getParentRoute: () => adminRoute,
  path: 'resources',
  component: () => <ResourcesPage role="admin" />,
})
const sellerResources = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'resources',
  component: () => <ResourcesPage role="seller" />,
})
const sellerAccount = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'account',
  component: () => <AccountPage shell="seller" />,
})
const supplyResources = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'resources',
  component: () => <ResourcesPage role="supply" />,
})

// Wave 7 Day 1: My Permissions (Priority 8)
const adminPermissions = createRoute({
  getParentRoute: () => adminRoute,
  path: 'my-permissions',
  component: () => <MyPermissionsPage role="admin" />,
})
// Wave 7 Day 2: Admin Users (Priority 8 admin view)
const adminUsers = createRoute({
  getParentRoute: () => adminRoute,
  path: 'users',
  component: AdminUsersPage,
})
// Wave 7 Day 3.3: Admin Wallets directory (cross-org treasury)
const adminWallets = createRoute({
  getParentRoute: () => adminRoute,
  path: 'wallets',
  component: AdminWalletsPage,
})

// Etsy Reviews + recovery tracking — admin consolidated list + settings.
const adminReviews = createRoute({
  getParentRoute: () => adminRoute,
  path: 'reviews',
  component: AdminReviews,
})
const adminReviewSettings = createRoute({
  getParentRoute: () => adminRoute,
  path: 'reviews/settings',
  component: ReviewTrackingSettings,
})
// Owner-only exit-plan profitability (exit_plan.view; the API refuses everyone else).
const adminExitPlan = createRoute({
  getParentRoute: () => adminRoute,
  path: 'exit-plan',
  component: ExitPlanPage,
})
const adminSalesGoals = createRoute({
  getParentRoute: () => adminRoute,
  path: 'sales-goals',
  component: SalesGoalsPage,
})

// Shipping V1 T1.9: Fulfillment Ecosystems read-only inventory
// (V1 invariant; backend resume sonrası CRUD per V2 amendment §A5).
const adminFulfillmentEcosystems = createRoute({
  getParentRoute: () => adminRoute,
  path: 'fulfillment-ecosystems',
  component: FulfillmentEcosystems,
})

// Wave 9 Day 1: Multi-Store Report (Priority 10 — Sarah cross-store)
const sellerMultiStoreReport = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'multi-store-report',
  component: MultiStoreReport,
})
const sellerPermissions = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'my-permissions',
  component: () => <MyPermissionsPage role="seller" />,
})
const supplyPermissions = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'my-permissions',
  component: () => <MyPermissionsPage role="supply" />,
})

// ─── Design Studio (Personalize It) Wave 1 routes ─────────────────────────────
const adminPersonalizeHome = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize',
  component: DesignStudioHome,
})
const adminPersonalizeCategories = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize/categories',
  component: CategoriesPage,
})
const adminPersonalizeTypes = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize/categories/$categoryId/types',
  component: CategoriesPage,
})
const adminPersonalizeThemes = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize/categories/$categoryId/types/$typeId/themes',
  component: CategoriesPage,
})
const adminPersonalizeDesigns = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize/categories/$categoryId/types/$typeId/themes/$themeId/designs',
  component: CategoriesPage,
})
const adminPersonalizeFonts = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize/fonts',
  component: FontsLibrary,
})
const adminPersonalizeFontApproval = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize/fonts/approval',
  component: FontApprovalQueue,
})
const adminPersonalizeMtsFonts = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize/jewelry-fonts',
  component: MtsFontsPage,
})
const adminPersonalizeSvgEditor = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize/svg-design/$designId',
  component: SvgEditor,
})
const adminPersonalizeComposer = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize/composer',
  component: Composer,
})
const adminPersonalizeAccess = createRoute({
  getParentRoute: () => adminRoute,
  path: 'personalize/access',
  component: AccessGrantGrid,
})

// ── Document Studio (Catalog Publishing) ──
const adminDocStudioCatalogs = createRoute({
  getParentRoute: () => adminRoute,
  path: 'document-studio/catalogs',
  component: DocStudioCatalogsPage,
})
const adminDocStudioFiles = createRoute({
  getParentRoute: () => adminRoute,
  path: 'document-studio/files',
  component: DocStudioFilesPage,
})
const adminDocStudioBrandPages = createRoute({
  getParentRoute: () => adminRoute,
  path: 'document-studio/brand-pages',
  component: DocStudioBrandPagesPage,
})
const adminDocStudioBrand = createRoute({
  getParentRoute: () => adminRoute,
  path: 'document-studio/brand',
  component: DocStudioBrandSettingsPage,
})

// ── Company Registrations (Start-a-U.S.-company admin surface) ──
const adminCompanyRegistrations = createRoute({
  getParentRoute: () => adminRoute,
  path: 'company-registrations',
  component: CompanyRegistrationsList,
})
const adminCompanyRegistrationDetail = createRoute({
  getParentRoute: () => adminRoute,
  path: 'company-registrations/$id',
  component: CompanyRegistrationDetail,
})

const sellerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'seller',
  component: SellerShell,
})
const sellerIndex = createRoute({
  getParentRoute: () => sellerRoute,
  path: '/',
  component: SellerHome,
})
const sellerMessages = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'messages',
  component: SellerMessagesPage,
})
const sellerMyProducts = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'my-products',
  // ?q= pre-fills the product search (topbar search "See all").
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === 'string' && search.q.trim() ? search.q : undefined,
  }),
  component: MyProducts,
})
const sellerMyListings = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'my-listings',
  // Filters live in the URL (?store ?status ?issues ?q ?sort); the seller home
  // "listings with issues" card links with ?status=issues.
  validateSearch: parseSellerListingsSearch,
  component: MyListings,
})
const sellerMyOrders = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'my-orders',
  // ShipStation-style status tabs drive a ?status= filter (real-mode My Orders).
  // ?store= narrows the list to one shop (sidebar store rows under Awaiting
  // Shipment); see features/seller/awaiting-shipment-stores.ts.
  validateSearch: (
    search: Record<string, unknown>,
  ): { status?: string; store?: string; q?: string } => ({
    status: typeof search.status === 'string' ? search.status : undefined,
    store: typeof search.store === 'string' ? search.store : undefined,
    // ?q= pre-fills the search box (topbar search "See all").
    q: typeof search.q === 'string' && search.q.trim() ? search.q : undefined,
  }),
  component: MyOrders,
})
// Dalga 3 (envanter akışı denetimi 2026-09-26) — seller read-only returns.
const sellerReturns = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'returns',
  component: SellerReturns,
})
const sellerStores = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'stores',
  component: SellerStoresPage,
})
const sellerStoreDetail = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'stores/$storeId',
  component: SellerStoreDetailPage,
})
const sellerScorecard = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'scorecard',
  component: () => <ScorecardPage scope="seller" />,
})
const sellerServers = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'servers',
  component: SellerServersPage,
})
const sellerMyOrderDetail = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'my-orders/$id',
  component: OrderDetail,
})
const sellerInventory = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'inventory',
  component: SellerInventory,
})
const SELLER_CATALOG_SORTS = new Set(['newest', 'price_asc', 'price_desc'])
const SELLER_CATALOG_TABS = new Set(['all', 'new', 'saved'])

type SellerCatalogSearch = {
  category?: string
  subcategory?: string
  q?: string
  sort?: 'newest' | 'price_asc' | 'price_desc'
  tab?: 'all' | 'new' | 'saved'
}

// Seller Catalogue — URL-driven taxonomy, search and merchandising controls.
const sellerCatalog = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'catalog',
  validateSearch: (search: Record<string, unknown>): SellerCatalogSearch => {
    const sort =
      typeof search.sort === 'string' && SELLER_CATALOG_SORTS.has(search.sort)
        ? (search.sort as SellerCatalogSearch['sort'])
        : undefined
    const tab =
      typeof search.tab === 'string' && SELLER_CATALOG_TABS.has(search.tab)
        ? (search.tab as SellerCatalogSearch['tab'])
        : undefined
    return {
      category: typeof search.category === 'string' ? search.category : undefined,
      subcategory:
        typeof search.subcategory === 'string' ? search.subcategory : undefined,
      q: typeof search.q === 'string' ? search.q : undefined,
      sort,
      tab,
    }
  },
  component: SellerCatalog,
})
// Listing Factory (Component B) — catalog "List in my store" + draft page.
// Kept at catalog/list; its CTAs fold into the vitrine detail page in Wave 2.
const sellerListInStore = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'catalog/list',
  component: ListInMyStore,
})
// Seller product detail — opens on a vitrine card click (static catalog/list is
// matched before this dynamic segment).
const sellerCatalogProductDetail = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'catalog/$productId',
  component: SellerProductDetail,
})
// V2 layout (clean / Printful-adapted) at a separate link so the classic page
// keeps working while we compare designs.
const sellerCatalogProductDetailV2 = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'catalog-v2/$productId',
  component: SellerProductDetailV2,
})
const sellerDraftListing = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'listings/$id',
  component: DraftListingPage,
})
const sellerMyReports = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'my-reports',
  component: MyReports,
})
const sellerMyReportsNew = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'my-reports/new',
  component: NewReport,
  validateSearch: (search: Record<string, unknown>) => ({
    product_id: typeof search.product_id === 'string' ? search.product_id : undefined,
    product_name:
      typeof search.product_name === 'string' ? search.product_name : undefined,
  }),
})
const sellerWorkspaceTasks = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'workspace/tasks',
  component: SellerStaffTasks,
})
const sellerWorkspaceTaskDetail = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'workspace/tasks/$taskId',
  component: SellerStaffTaskDetail,
})
const sellerWorkspaceProjectDetail = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'workspace/tasks/projects/$projectId',
  component: SellerStaffProjectDetail,
})
const sellerWalletIndex = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'wallet',
  component: Payments,
})
const sellerWalletPayments = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'wallet/payments',
  component: Payments,
})
const sellerWalletReferral = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'wallet/referral',
  component: Referral,
})
const sellerWalletBanking = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'wallet/banking',
  component: BankingDetails,
})
// Wallet → Subscription (Volkan 2026-11-01): the monthly fee, its card, invoices.
const sellerWalletSubscription = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'wallet/subscription',
  component: SellerSubscriptionPage,
})
const sellerWalletAddFunds = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'wallet/add-funds',
  component: AddFunds,
})
const sellerWalletPayouts = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'wallet/payouts',
  component: PayoutSetup,
})
const sellerActivity = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'activity',
  component: ActivitySeller,
})
// Etsy Reviews + recovery tracking — seller own-org list.
const sellerReviews = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'reviews',
  component: SellerReviews,
})
const sellerSettings = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'settings',
  validateSearch: (s: Record<string, unknown>): { section?: string } => ({
    section: typeof s.section === 'string' ? s.section : undefined,
  }),
  component: () => <Settings surface="seller" />,
})

// Listing Factory — Scene & Persona Builder (Component A)
const sellerScenes = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'scenes',
  component: SceneBuilder,
})

// Seller Personalize (Wave 4 PEXIFY-EXTENSION cross-side)
const sellerPersonalizeHome = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'personalize',
  component: SellerPersonalizeHome,
})
const sellerPersonalizeTemplates = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'personalize/templates',
  component: SellerPersonalizeTemplates,
})
const sellerPersonalizeBindings = createRoute({
  getParentRoute: () => sellerRoute,
  path: 'personalize/bindings',
  component: SellerPersonalizeBindings,
})

const supplyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'supply',
  component: SupplyShell,
})
const supplyIndex = createRoute({
  getParentRoute: () => supplyRoute,
  path: '/',
  component: SupplyHome,
})
const supplyMessages = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'messages',
  component: SupplyMessagesPage,
})
const supplyMyUpdates = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'my-updates',
  component: MySubmittedUpdates,
})
const supplyMyUpdatesNew = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'my-updates/new',
  component: SubmitNewUpdate,
})
const supplyWorkOrders = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'work-orders',
  component: MyWorkOrders,
})
const supplyWorkOrderDetail = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'work-orders/$id',
  component: WorkOrderDetail,
})
// M4 Custody & Transfer — workshop Prepare & Pack (Phase 1).
const supplyPreparePack = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'prepare-pack',
  component: WorkshopPreparePack,
})
const supplyAccount = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'account',
  component: () => <AccountPage shell="supply" />,
})
// Sample domain (Sampler port) — supply = factory.
const supplySamplesList = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'samples',
  component: SupplySamplesList,
})
const supplySampleDetail = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'samples/$id',
  component: SupplySampleDetail,
})
const supplyPOInbox = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'po-inbox',
  component: SupplyPurchaseOrderInbox,
})
// T1.11 shipping-v1: Today's Print Queue (Emin NJ depot scan/dispatch focus).
// V1 skeleton — Phase 3+ wires actual shipment queue data + functional date
// filter. Mounted under supply shell per T1.10 adaptation (dedicated
// /warehouse shell deferred to Wave-16 Madde 15). Capability gate inside
// component: ship.shipment.scan.
const supplyPrintQueue = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'print-queue',
  component: TodaysPrintQueue,
})
const supplyWorkspaceTasks = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'workspace/tasks',
  component: SupplyStaffTasks,
})
const supplyWorkspaceTaskDetail = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'workspace/tasks/$taskId',
  component: SupplyStaffTaskDetail,
})
const supplyWorkspaceProjectDetail = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'workspace/tasks/projects/$projectId',
  component: SupplyStaffProjectDetail,
})
const supplyWalletIndex = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'wallet',
  component: Payments,
})
const supplyWalletPayments = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'wallet/payments',
  component: Payments,
})
const supplyWalletReferral = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'wallet/referral',
  component: Referral,
})
const supplyWalletBanking = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'wallet/banking',
  component: BankingDetails,
})
const supplyWalletPayouts = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'wallet/payouts',
  component: PayoutSetup,
})
const supplyActivity = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'activity',
  component: ActivitySupply,
})
const supplySettings = createRoute({
  getParentRoute: () => supplyRoute,
  path: 'settings',
  component: () => <Settings surface="supply" />,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  publicAcceptInvitation,
  publicSelfApply,
  publicStartCompany,
  publicStartCompanyAlias,
  publicWelcome,
  publicLogin,
  publicForgotPassword,
  publicResetPassword,
  adminRoute.addChildren([
    adminIndex,
    adminInbox,
    adminServers,
    adminStores,
    adminScorecard,
    adminHubReceiving,
    adminAccount,
    adminPendingUpdates,
    adminPendingUpdateDetail,
    adminVendorReplacements,
    adminVendorReplacementNew,
    adminVendorReplacementDetail,
    adminAssortment,
    adminB2BPushList,
    adminB2BPushNew,
    adminB2BPushDetail,
    adminBO3PortalPreview,
    adminCatalogIssues,
    adminDemoGuide,
    adminInventory,
    adminInventoryProducts,
    adminInventoryMovements,
    adminUnmappedSkus,
    adminInventoryImport,
    adminCatalogSeedImport,
    adminUngroupedProducts,
    adminLegacyEtsyCleanup,
    adminInventoryBackorders,
    adminInventoryExternalFulfillment,
    adminInventoryValuation,
    adminProducerQueue,
    adminOrders,
    adminOrderDetail,
    adminWorkOrderPrintLabel,
    adminPONew,
    adminPOList,
    adminPODetail,
    adminPOReceive,
    adminPOReceiveScan,
    adminPOQC,
    adminPODispute,
    adminReturnsQueue,
    adminReturnProcessing,
    adminWorkspaceTasks,
    adminWorkspaceTaskDetail,
    adminWorkspaceProjectDetail,
    adminWalletIndex,
    adminWalletPayments,
    adminWalletReferral,
    adminBankApprovals,
    adminPaymentGateways,
    adminBank,
    adminPolicies,
    adminActivity,
    adminReports,
    adminEmployeeManagement,
    adminCatalogProducts,
    adminCatalogBundles,
    adminCatalogProductDetail,
    adminCatalogCategories,
    adminCatalogMeasurementQueue,
  adminCatalogMarketplaceListings,
  adminSupplierCatalog,
    adminCatalogNormalization,
    adminGirginImport,
    adminCatalogSkuAliases,
    adminOnboardingList,
    adminOnboardingDetail,
    adminSamplesList,
    adminSamplesTrash,
    adminSamplesBulkAdopt,
    adminSampleDetail,
    adminSelfApplyList,
    adminPartnerSettlements,
    adminMonetization,
    adminFeesAndCharges,
    adminIncomingWires,
    adminSubscriptions,
    adminShippingSetup,
    adminDriveTriage,
    adminListingTriage,
    adminDriveMaintenance,
    adminShippingDemo,
    adminShippingLiveDemo,
    adminDriveExplorer,
    adminDriveAudit,
    adminDriveFiles,
    adminPricingHub,
    adminPricing,
  adminPricingMatrix,
    adminEtsyProfit,
    adminEtsyResearchKeyword,
    adminEtsyResearchToday,
    adminEtsyResearchShop,
    adminEtsyResearchReviews,
    adminEtsyResearchListing,
    adminEtsyTrends,
    adminUsCalendar,
    adminTrainings,
    adminLeads,
    adminLeadsScoreboard,
    adminLeadDetail,
    adminResources,
    adminPermissions,
    adminUsers,
    adminWallets,
    adminReviews,
    adminReviewSettings,
    adminSalesGoals,
    adminExitPlan,
    adminFulfillmentEcosystems,
    adminPersonalizeHome,
    adminPersonalizeCategories,
    adminPersonalizeTypes,
    adminPersonalizeThemes,
    adminPersonalizeDesigns,
    adminPersonalizeFonts,
    adminPersonalizeFontApproval,
    adminPersonalizeMtsFonts,
    adminPersonalizeSvgEditor,
    adminPersonalizeComposer,
    adminPersonalizeAccess,
    adminDocStudioCatalogs,
    adminDocStudioFiles,
    adminDocStudioBrandPages,
    adminDocStudioBrand,
    adminCompanyRegistrations,
    adminCompanyRegistrationDetail,
    adminSettings,
  ]),
  sellerRoute.addChildren([
    sellerIndex,
    sellerMessages,
    sellerStores,
    sellerStoreDetail,
    sellerScorecard,
    sellerServers,
    sellerInventory,
    sellerCatalog,
    sellerListInStore,
    sellerCatalogProductDetail,
    sellerCatalogProductDetailV2,
    sellerDraftListing,
    sellerMyProducts,
    sellerMyListings,
    sellerMyOrders,
    sellerReturns,
    sellerMyOrderDetail,
    sellerMyReports,
    sellerMyReportsNew,
    sellerWorkspaceTasks,
    sellerWorkspaceTaskDetail,
    sellerWorkspaceProjectDetail,
    sellerWalletIndex,
    sellerWalletPayments,
    sellerWalletReferral,
    sellerWalletBanking,
    sellerWalletAddFunds,
    sellerWalletSubscription,
    sellerWalletPayouts,
    sellerActivity,
    sellerReviews,
    sellerScenes,
    sellerPersonalizeHome,
    sellerPersonalizeTemplates,
    sellerPersonalizeBindings,
    sellerSettings,
    sellerResources,
    sellerAccount,
    sellerPermissions,
    sellerMultiStoreReport,
    sellerEtsyProfit,
    sellerEtsyResearchKeyword,
    sellerEtsyResearchToday,
    sellerEtsyResearchShop,
    sellerEtsyResearchReviews,
    sellerEtsyResearchListing,
    sellerEtsyTrends,
    sellerUsCalendar,
    sellerDriveFiles,
  ]),
  supplyRoute.addChildren([
    supplyIndex,
    supplyMessages,
    supplyMyUpdates,
    supplyMyUpdatesNew,
    supplyWorkOrders,
    supplyWorkOrderDetail,
    supplyPreparePack,
    supplyAccount,
    supplySamplesList,
    supplySampleDetail,
    supplyPOInbox,
    supplyPrintQueue,
    supplyWorkspaceTasks,
    supplyWorkspaceTaskDetail,
    supplyWorkspaceProjectDetail,
    supplyWalletIndex,
    supplyWalletPayments,
    supplyWalletReferral,
    supplyWalletBanking,
    supplyWalletPayouts,
    supplyActivity,
    supplySettings,
    supplyResources,
    supplyPermissions,
    supplyDriveFiles,
  ]),
])

// basepath derived from Vite BASE_URL ("/m0/" in prod, "/" in dev).
// TanStack Router expects no trailing slash.
const basepath = import.meta.env.BASE_URL.replace(/\/$/, '') || undefined

export const router = createRouter({ routeTree, basepath })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
